import { motion } from "framer-motion";
import {Lock, Shield, Eye, Database, UserCheck, AlertCircle} from 'lucide-react';
import { AnimatedSection } from "../shared/AnimatedSection";
import { typography, textColors, fontWeights } from "../../styles/typography";

export const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900 py-20 px-6">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <Lock className="w-16 h-16 text-purple-400 mx-auto mb-6" />
          <h1 className={`${typography.h1Light} mb-4`}>
            Política de Privacidad
          </h1>
          <p className={`${typography.bodyLarge} ${textColors.secondaryLight}`}>
            ᏃꮎꭼᎱꭼꮮ IA™ - Última actualización: {new Date().toLocaleDateString("es-ES")}
          </p>
        </motion.div>

        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 md:p-12 text-white space-y-8">
          <AnimatedSection>
            <div className="bg-purple-500/20 border border-purple-400 rounded-xl p-6 mb-8">
              <div className="flex items-start gap-4">
                <Shield className="w-8 h-8 text-purple-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className={`${typography.h5Light} mb-3`}>Nuestro Compromiso con su Privacidad</h3>
                  <p className={`${typography.bodyBaseLight}`}>
                    En ᏃꮎꭼᎱꭼꮮ IA™, la protección de sus datos personales es una prioridad fundamental. Esta Política de Privacidad explica cómo recopilamos, usamos, almacenamos y protegemos su información personal en cumplimiento con las leyes aplicables: GDPR (Europa), LFPDPPP (México), CCPA (California), y otras regulaciones internacionales.
                  </p>
                </div>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection>
            <div className="flex items-start gap-4 mb-4">
              <Database className="w-6 h-6 text-purple-400 flex-shrink-0 mt-1" />
              <div>
                <h2 className={`${typography.h3Light} mb-4`}>1. Información que Recopilamos</h2>
                
                <h3 className={`${typography.h5Light} text-purple-300 mb-3 mt-6`}>1.1 Información Proporcionada Directamente</h3>
                <p className={`${typography.bodyBaseLight} mb-3`}>
                  Cuando usted se registra o utiliza nuestros servicios, recopilamos:
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4 mb-4">
                  <li><strong>Datos de Identificación:</strong> Nombre completo, correo electrónico, número de teléfono</li>
                  <li><strong>Datos de la Empresa:</strong> Nombre de la empresa, industria, tamaño, sitio web</li>
                  <li><strong>Datos de Facturación:</strong> Dirección de facturación, RFC/Tax ID, información de pago</li>
                  <li><strong>Credenciales de Acceso:</strong> Usuario, contraseña encriptada, preferencias de cuenta</li>
                  <li><strong>Contenido del Usuario:</strong> Datos de CRM, leads, conversaciones, archivos subidos</li>
                </ul>

                <h3 className={`${typography.h5Light} text-purple-300 mb-3 mt-6`}>1.2 Información Recopilada Automáticamente</h3>
                <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4 mb-4">
                  <li><strong>Datos de Uso:</strong> Páginas visitadas, funciones utilizadas, tiempo de sesión</li>
                  <li><strong>Datos Técnicos:</strong> Dirección IP, tipo de navegador, dispositivo, sistema operativo</li>
                  <li><strong>Cookies y Tecnologías Similares:</strong> Identificadores únicos, preferencias, sesiones</li>
                  <li><strong>Datos de Comunicación:</strong> Mensajes de WhatsApp, emails, SMS enviados a través de la plataforma</li>
                </ul>

                <h3 className={`${typography.h5Light} text-purple-300 mb-3 mt-6`}>1.3 Información de Terceros</h3>
                <p className={`${typography.bodyBaseLight} mb-3`}>
                  Podemos recibir información de servicios integrados:
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                  <li><strong>Meta Platforms:</strong> Datos de Lead Ads, Messenger, WhatsApp Business</li>
                  <li><strong>Google:</strong> Datos de Google Ads, Analytics, Gmail</li>
                  <li><strong>LinkedIn:</strong> Datos de campañas publicitarias</li>
                  <li><strong>Proveedores de Pago:</strong> Confirmaciones de transacciones (sin datos de tarjeta)</li>
                </ul>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection>
            <div className="flex items-start gap-4 mb-4">
              <Eye className="w-6 h-6 text-purple-400 flex-shrink-0 mt-1" />
              <div>
                <h2 className={`${typography.h3Light} mb-4`}>2. Cómo Utilizamos su Información</h2>
                
                <h3 className={`${typography.h5Light} text-purple-300 mb-3 mt-6`}>2.1 Prestación del Servicio</h3>
                <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4 mb-4">
                  <li>Crear y gestionar su cuenta de usuario</li>
                  <li>Proporcionar acceso a la plataforma y sus funcionalidades</li>
                  <li>Procesar y ejecutar sus campañas de marketing automatizado</li>
                  <li>Gestionar comunicaciones con sus leads y clientes</li>
                  <li>Entrenar y mejorar nuestros agentes de IA conversacional</li>
                </ul>

                <h3 className={`${typography.h5Light} text-purple-300 mb-3 mt-6`}>2.2 Facturación y Pagos</h3>
                <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4 mb-4">
                  <li>Procesar pagos de suscripciones y cargos por uso</li>
                  <li>Emitir facturas y recibos fiscales</li>
                  <li>Prevenir fraude y actividades no autorizadas</li>
                  <li>Gestionar reembolsos según nuestra política de garantía</li>
                </ul>

                <h3 className={`${typography.h5Light} text-purple-300 mb-3 mt-6`}>2.3 Comunicación y Soporte</h3>
                <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4 mb-4">
                  <li>Responder a sus consultas y solicitudes de soporte</li>
                  <li>Enviar notificaciones importantes sobre el servicio</li>
                  <li>Informar sobre actualizaciones, nuevas funcionalidades</li>
                  <li>Enviar comunicaciones de marketing (con su consentimiento)</li>
                </ul>

                <h3 className={`${typography.h5Light} text-purple-300 mb-3 mt-6`}>2.4 Mejora del Servicio</h3>
                <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4 mb-4">
                  <li>Analizar patrones de uso para mejorar funcionalidades</li>
                  <li>Realizar pruebas A/B y optimizaciones</li>
                  <li>Desarrollar nuevas características basadas en feedback</li>
                  <li>Entrenar modelos de IA con datos anonimizados</li>
                </ul>

                <h3 className={`${typography.h5Light} text-purple-300 mb-3 mt-6`}>2.5 Cumplimiento Legal</h3>
                <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                  <li>Cumplir con obligaciones legales y regulatorias</li>
                  <li>Responder a requerimientos de autoridades competentes</li>
                  <li>Proteger nuestros derechos legales y propiedad</li>
                  <li>Prevenir actividades ilegales o fraudulentas</li>
                </ul>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection>
            <div className="flex items-start gap-4 mb-4">
              <UserCheck className="w-6 h-6 text-purple-400 flex-shrink-0 mt-1" />
              <div>
                <h2 className={`${typography.h3Light} mb-4`}>3. Base Legal para el Tratamiento</h2>
                <p className={`${typography.bodyBaseLight} mb-4`}>
                  Procesamos sus datos personales bajo las siguientes bases legales:
                </p>
                
                <div className="space-y-4">
                  <div className="bg-white/5 rounded-xl p-6">
                    <h3 className={`${typography.h6Light} mb-2`}>3.1 Ejecución de Contrato</h3>
                    <p className={`${typography.bodyBaseLight}`}>
                      El procesamiento es necesario para ejecutar el contrato de servicios que usted acepta al registrarse.
                    </p>
                  </div>

                  <div className="bg-white/5 rounded-xl p-6">
                    <h3 className={`${typography.h6Light} mb-2`}>3.2 Consentimiento</h3>
                    <p className={`${typography.bodyBaseLight}`}>
                      Usted ha dado su consentimiento explícito para el procesamiento de datos específicos (ej. marketing).
                    </p>
                  </div>

                  <div className="bg-white/5 rounded-xl p-6">
                    <h3 className={`${typography.h6Light} mb-2`}>3.3 Interés Legítimo</h3>
                    <p className={`${typography.bodyBaseLight}`}>
                      Tenemos un interés legítimo en mejorar nuestros servicios, prevenir fraude, y garantizar la seguridad.
                    </p>
                  </div>

                  <div className="bg-white/5 rounded-xl p-6">
                    <h3 className={`${typography.h6Light} mb-2`}>3.4 Obligación Legal</h3>
                    <p className={`${typography.bodyBaseLight}`}>
                      Debemos cumplir con obligaciones legales (ej. retención de datos fiscales, reportes regulatorios).
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection>
            <h2 className={`${typography.h3Light} mb-4`}>4. Compartir Información con Terceros</h2>
            <p className={`${typography.bodyBaseLight} mb-4`}>
              <strong>NO vendemos sus datos personales a terceros.</strong> Compartimos información únicamente en las siguientes circunstancias:
            </p>
            
            <div className="space-y-4">
              <div className="bg-white/5 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-2">4.1 Proveedores de Servicios</h3>
                <p className="text-gray-300 mb-3">
                  Compartimos datos con proveedores que procesan información en nuestro nombre:
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
                  <li>Procesadores de pago (Stripe, PayPal)</li>
                  <li>Servicios de hosting y almacenamiento (AWS, Google Cloud)</li>
                  <li>Herramientas de análisis (Google Analytics, Mixpanel)</li>
                  <li>Plataformas de comunicación (Twilio, SendGrid)</li>
                </ul>
              </div>

              <div className="bg-white/5 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-2">4.2 Integraciones Autorizadas</h3>
                <p className="text-gray-300">
                  Cuando usted conecta servicios de terceros (Meta, Google, LinkedIn), compartimos datos necesarios según su autorización explícita.
                </p>
              </div>

              <div className="bg-white/5 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-2">4.3 Requerimientos Legales</h3>
                <p className="text-gray-300">
                  Podemos divulgar información si es requerido por ley, orden judicial, o para proteger derechos legales.
                </p>
              </div>

              <div className="bg-white/5 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-2">4.4 Transferencias Empresariales</h3>
                <p className="text-gray-300">
                  En caso de fusión, adquisición o venta de activos, sus datos pueden transferirse al nuevo propietario.
                </p>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection>
            <h2 className={`${typography.h3Light} mb-4`}>5. Seguridad de los Datos</h2>
            <p className={`${typography.bodyBaseLight} mb-4`}>
              Implementamos medidas técnicas y organizativas para proteger sus datos:
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-emerald-500/20 border border-emerald-400 rounded-xl p-6">
                <h3 className="text-lg font-bold text-emerald-300 mb-3">Medidas Técnicas</h3>
                <ul className="list-disc list-inside text-gray-300 space-y-2 text-sm">
                  <li>Encriptación SSL/TLS en tránsito</li>
                  <li>Encriptación AES-256 en reposo</li>
                  <li>Autenticación de dos factores (2FA)</li>
                  <li>Firewalls y sistemas de detección de intrusos</li>
                  <li>Backups automáticos cifrados</li>
                  <li>Auditorías de seguridad regulares</li>
                </ul>
              </div>

              <div className="bg-blue-500/20 border border-blue-400 rounded-xl p-6">
                <h3 className="text-lg font-bold text-blue-300 mb-3">Medidas Organizativas</h3>
                <ul className="list-disc list-inside text-gray-300 space-y-2 text-sm">
                  <li>Acceso restringido por roles</li>
                  <li>Políticas de contraseñas robustas</li>
                  <li>Capacitación en seguridad del personal</li>
                  <li>Acuerdos de confidencialidad</li>
                  <li>Procedimientos de respuesta a incidentes</li>
                  <li>Evaluaciones de riesgo periódicas</li>
                </ul>
              </div>
            </div>

            <div className="bg-orange-500/20 border border-orange-400 rounded-xl p-6 mt-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-orange-400 flex-shrink-0 mt-1" />
                <div className="text-gray-300">
                  <p className="font-bold text-white mb-2">Importante:</p>
                  <p>
                    Ningún sistema es 100% seguro. Aunque implementamos las mejores prácticas de la industria, no podemos garantizar seguridad absoluta. Usted es responsable de mantener la confidencialidad de sus credenciales de acceso.
                  </p>
                </div>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection>
            <h2 className={`${typography.h3Light} mb-4`}>6. Retención de Datos</h2>
            <p className={`${typography.bodyBaseLight} mb-4`}>
              Conservamos sus datos personales durante el tiempo necesario para cumplir con los fines descritos:
            </p>
            
            <div className="space-y-3">
              <div className="bg-white/5 rounded-xl p-5">
                <p className="text-white font-semibold mb-1">Datos de Cuenta Activa</p>
                <p className="text-gray-300 text-sm">Durante la vigencia de su suscripción + 90 días tras cancelación</p>
              </div>
              <div className="bg-white/5 rounded-xl p-5">
                <p className="text-white font-semibold mb-1">Datos de Facturación</p>
                <p className="text-gray-300 text-sm">7 años (requisito fiscal legal)</p>
              </div>
              <div className="bg-white/5 rounded-xl p-5">
                <p className="text-white font-semibold mb-1">Datos de CRM y Leads</p>
                <p className="text-gray-300 text-sm">Hasta que solicite eliminación o 2 años de inactividad</p>
              </div>
              <div className="bg-white/5 rounded-xl p-5">
                <p className="text-white font-semibold mb-1">Logs de Seguridad</p>
                <p className="text-gray-300 text-sm">12 meses (requisito de auditoría)</p>
              </div>
              <div className="bg-white/5 rounded-xl p-5">
                <p className="text-white font-semibold mb-1">Datos Anonimizados</p>
                <p className="text-gray-300 text-sm">Indefinidamente para análisis estadístico</p>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection>
            <h2 className={`${typography.h3Light} mb-4`}>7. Sus Derechos de Privacidad</h2>
            <p className={`${typography.bodyBaseLight} mb-4`}>
              Usted tiene los siguientes derechos sobre sus datos personales:
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white/5 rounded-xl p-5">
                <h3 className="text-white font-bold mb-2">✅ Derecho de Acceso</h3>
                <p className="text-gray-300 text-sm">Solicitar una copia de sus datos personales</p>
              </div>
              <div className="bg-white/5 rounded-xl p-5">
                <h3 className="text-white font-bold mb-2">✏️ Derecho de Rectificación</h3>
                <p className="text-gray-300 text-sm">Corregir datos inexactos o incompletos</p>
              </div>
              <div className="bg-white/5 rounded-xl p-5">
                <h3 className="text-white font-bold mb-2">🗑️ Derecho de Supresión</h3>
                <p className="text-gray-300 text-sm">Solicitar eliminación de sus datos ("derecho al olvido")</p>
              </div>
              <div className="bg-white/5 rounded-xl p-5">
                <h3 className="text-white font-bold mb-2">🚫 Derecho de Oposición</h3>
                <p className="text-gray-300 text-sm">Oponerse al procesamiento de sus datos</p>
              </div>
              <div className="bg-white/5 rounded-xl p-5">
                <h3 className="text-white font-bold mb-2">📦 Derecho de Portabilidad</h3>
                <p className="text-gray-300 text-sm">Recibir sus datos en formato estructurado</p>
              </div>
              <div className="bg-white/5 rounded-xl p-5">
                <h3 className="text-white font-bold mb-2">⏸️ Derecho de Limitación</h3>
                <p className="text-gray-300 text-sm">Restringir el procesamiento en ciertos casos</p>
              </div>
            </div>

            <div className="bg-purple-500/20 border border-purple-400 rounded-xl p-6 mt-6">
              <h3 className={`${typography.h5Light} mb-3`}>Cómo Ejercer sus Derechos</h3>
              <p className={`${typography.bodyBaseLight} mb-3`}>
                Para ejercer cualquiera de estos derechos, contacte a nuestro Oficial de Privacidad:
              </p>
              <p className="text-white font-semibold">Email: <a href="mailto:privacy@zoepelai.com" className="text-purple-300 hover:text-purple-200 underline">privacy@zoepelai.com</a></p>
              <p className="text-gray-300 text-sm mt-2">Responderemos en un plazo máximo de 30 días</p>
            </div>
          </AnimatedSection>

          <AnimatedSection>
            <h2 className={`${typography.h3Light} mb-4`}>8. Cookies y Tecnologías de Rastreo</h2>
            <p className={`${typography.bodyBaseLight} mb-4`}>
              Utilizamos cookies y tecnologías similares para mejorar su experiencia:
            </p>
            
            <div className="space-y-4">
              <div className="bg-white/5 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-2">8.1 Cookies Esenciales (Obligatorias)</h3>
                <p className="text-gray-300 text-sm mb-2">
                  Necesarias para el funcionamiento básico de la plataforma: sesiones de usuario, autenticación, seguridad.
                </p>
                <p className="text-gray-400 text-xs">No requieren consentimiento según regulaciones.</p>
              </div>

              <div className="bg-white/5 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-2">8.2 Cookies de Rendimiento (Opcionales)</h3>
                <p className="text-gray-300 text-sm mb-2">
                  Analizar cómo los usuarios interactúan con la plataforma para mejorar funcionalidades.
                </p>
                <p className="text-gray-400 text-xs">Requieren su consentimiento explícito.</p>
              </div>

              <div className="bg-white/5 rounded-xl p-6">
                <h3 className="text-lg font-bold text-white mb-2">8.3 Cookies de Marketing (Opcionales)</h3>
                <p className="text-gray-300 text-sm mb-2">
                  Personalizar anuncios y medir efectividad de campañas publicitarias.
                </p>
                <p className="text-gray-400 text-xs">Requieren su consentimiento explícito.</p>
              </div>
            </div>

            <p className="text-gray-300 text-sm mt-4">
              Puede gestionar sus preferencias de cookies en cualquier momento desde la configuración de su navegador o nuestro panel de consentimiento.
            </p>
          </AnimatedSection>

          <AnimatedSection>
            <h2 className={`${typography.h3Light} mb-4`}>9. Transferencias Internacionales</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Sus datos pueden ser transferidos y almacenados en servidores ubicados fuera de su país de residencia, incluyendo Estados Unidos y la Unión Europea.
            </p>
            <p className="text-gray-300 leading-relaxed mb-4">
              <strong>Salvaguardias:</strong> Implementamos cláusulas contractuales estándar (SCC) aprobadas por la Comisión Europea y otros mecanismos de transferencia legalmente reconocidos para garantizar un nivel adecuado de protección.
            </p>
          </AnimatedSection>

          <AnimatedSection>
            <h2 className={`${typography.h3Light} mb-4`}>10. Privacidad de Menores</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Nuestros servicios NO están dirigidos a menores de 18 años. No recopilamos intencionalmente datos personales de menores.
            </p>
            <p className="text-gray-300 leading-relaxed">
              Si descubrimos que hemos recopilado datos de un menor sin consentimiento parental verificable, eliminaremos esa información inmediatamente. Contacte a <a href="mailto:privacy@zoepelai.com" className="text-purple-300 hover:text-purple-200 underline">privacy@zoepelai.com</a> si tiene conocimiento de tal situación.
            </p>
          </AnimatedSection>

          <AnimatedSection>
            <h2 className={`${typography.h3Light} mb-4`}>11. Cambios a esta Política</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Podemos actualizar esta Política de Privacidad periódicamente para reflejar cambios en nuestras prácticas o por requisitos legales.
            </p>
            <p className="text-gray-300 leading-relaxed mb-4">
              <strong>Notificación:</strong> Los cambios significativos serán notificados por correo electrónico con al menos 30 días de antelación. La versión actualizada incluirá la fecha de "Última actualización" al inicio del documento.
            </p>
            <p className="text-gray-300 leading-relaxed">
              Su uso continuado del Servicio después de la notificación constituye su aceptación de la política actualizada.
            </p>
          </AnimatedSection>

          <AnimatedSection>
            <h2 className={`${typography.h3Light} mb-4`}>12. Contacto - Oficial de Privacidad</h2>
            <div className="bg-purple-500/20 border border-purple-400 rounded-xl p-8">
              <p className="text-gray-300 mb-4">
                Para cualquier consulta, solicitud o inquietud relacionada con privacidad y protección de datos:
              </p>
              <div className="space-y-3">
                <p className="text-white font-semibold text-lg">Oficial de Privacidad - ᏃꮎꭼᎱꭼꮮ IA™</p>
                <p className="text-gray-300">
                  <strong>Email:</strong> <a href="mailto:privacy@zoepelai.com" className="text-purple-300 hover:text-purple-200 underline">privacy@zoepelai.com</a>
                </p>
                <p className="text-gray-300">
                  <strong>Asunto sugerido:</strong> "Solicitud de Privacidad - [Su Nombre]"
                </p>
                <p className="text-gray-300 text-sm mt-4">
                  Responderemos a su solicitud en un plazo máximo de 30 días. Para solicitudes complejas, podemos extender el plazo a 60 días con notificación previa.
                </p>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection>
            <h2 className={`${typography.h3Light} mb-4`}>13. Autoridad de Control</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Si considera que sus derechos de privacidad han sido vulnerados, tiene derecho a presentar una queja ante la autoridad de protección de datos de su jurisdicción:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li><strong>México:</strong> INAI (Instituto Nacional de Transparencia, Acceso a la Información y Protección de Datos Personales)</li>
              <li><strong>Unión Europea:</strong> Su Autoridad de Protección de Datos nacional</li>
              <li><strong>California (EE.UU.):</strong> California Attorney General's Office</li>
            </ul>
          </AnimatedSection>

          <div className="border-t border-white/20 pt-8 mt-12">
            <p className="text-center text-gray-400 text-sm">
              Última actualización: {new Date().toLocaleDateString("es-ES", { year: "numeric", month: "long", day: "numeric" })}
            </p>
            <p className="text-center text-gray-400 text-sm mt-2">
              © {new Date().getFullYear()} ᏃꮎꭼᎱꭼꮮ IA™. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
